import React, { useState, useEffect } from 'react';
import { configApi } from '../../services/api';
import { ConfigCategory, ConfigItem, UpdateConfigRequest } from '../../types';
import Button from '../../components/common/Button';
import ResultModal from '../../components/common/ResultModal';
import './Configuration.css';

const Configuration: React.FC = () => {
  const [categories, setCategories] = useState<ConfigCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map());

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalVariant, setModalVariant] = useState<'success' | 'error' | 'warning'>('success');

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const data = await configApi.getAll();
      setCategories(data);
    } catch (error: any) {
      setModalTitle('Error');
      setModalContent(<div>Failed to load configuration: {error.message}</div>);
      setModalVariant('error');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (configKey: string, newValue: string) => {
    const newMap = new Map(editedValues);
    newMap.set(configKey, newValue);
    setEditedValues(newMap);
  };

  const getValue = (item: ConfigItem): string => {
    return editedValues.get(item.configKey) ?? item.configValue;
  };

  const hasChanges = (): boolean => {
    return editedValues.size > 0;
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      setModalTitle('No Changes');
      setModalContent(<div>No configuration values have been modified.</div>);
      setModalVariant('warning');
      setShowModal(true);
      return;
    }

    setSaving(true);
    try {
      const updates: UpdateConfigRequest[] = Array.from(editedValues.entries()).map(
        ([configKey, configValue]) => ({
          configKey,
          configValue,
          updatedBy: 'Frontend User',
        })
      );

      await configApi.updateMany(updates);
      await configApi.reload();

      setModalTitle('Success');
      setModalContent(
        <div>
          <p><strong>{updates.length}</strong> configuration value(s) updated successfully.</p>
          <p className="text-muted">Configuration cache has been reloaded.</p>
        </div>
      );
      setModalVariant('success');
      setShowModal(true);

      // Clear edited values and reload
      setEditedValues(new Map());
      await loadConfiguration();
    } catch (error: any) {
      setModalTitle('Error');
      setModalContent(<div>Failed to save configuration: {error.message}</div>);
      setModalVariant('error');
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEditedValues(new Map());
  };

  const renderInput = (item: ConfigItem) => {
    const value = getValue(item);
    const isEdited = editedValues.has(item.configKey);

    switch (item.valueType.toLowerCase()) {
      case 'bool':
        return (
          <select
            value={value}
            onChange={(e) => handleValueChange(item.configKey, e.target.value)}
            className={`config-input ${isEdited ? 'edited' : ''}`}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case 'int':
        return (
          <input
            type="number"
            step="1"
            value={value}
            onChange={(e) => handleValueChange(item.configKey, e.target.value)}
            className={`config-input ${isEdited ? 'edited' : ''}`}
          />
        );

      case 'decimal':
        return (
          <input
            type="number"
            step="0.001"
            value={value}
            onChange={(e) => handleValueChange(item.configKey, e.target.value)}
            className={`config-input ${isEdited ? 'edited' : ''}`}
          />
        );

      default: // string
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleValueChange(item.configKey, e.target.value)}
            className={`config-input ${isEdited ? 'edited' : ''}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="configuration-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h2>System Configuration</h2>
          <p className="page-description">
            Manage trading system parameters and monitor settings
          </p>
        </div>
        <div className="header-actions">
          {hasChanges() && (
            <Button onClick={handleReset} variant="secondary">
              Reset Changes
            </Button>
          )}
          <Button onClick={handleSave} loading={saving} disabled={!hasChanges()}>
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Configuration Categories */}
      <div className="config-categories">
        {categories.map((category) => (
          <div key={category.category} className="config-category-card">
            <h3 className="category-title">{category.category}</h3>

            <div className="config-items">
              {category.items.map((item) => (
                <div key={item.configKey} className="config-item">
                  <div className="config-item-header">
                    <label className="config-label">
                      {item.configKey.split('.').pop()}
                    </label>
                    <span className="config-type">{item.valueType}</span>
                  </div>

                  {item.description && (
                    <p className="config-description">{item.description}</p>
                  )}

                  <div className="config-input-wrapper">
                    {renderInput(item)}
                  </div>

                  <div className="config-meta">
                    <span className="config-key">{item.configKey}</span>
                    {item.updatedAt && (
                      <span className="config-updated">
                        Updated: {new Date(item.updatedAt).toLocaleString()}
                        {item.updatedBy && ` by ${item.updatedBy}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Result Modal */}
      <ResultModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        variant={modalVariant}
      >
        {modalContent}
      </ResultModal>
    </div>
  );
};

export default Configuration;
